/**
 * Product Gallery
 */

const productGallery = () => {
  const config = {
    dom: {
      mediaVariantId: "data-media-variant-id",
      productImages: "productImages",
      productImagesLoading: "product-images__loading",
      productThumbnailsLoading: "product-thumbnails__loading",
      productThumbnails: "productThumbnails",
      productVariantLabel: "product-variant__label",
      youtube: "js-youtube",
      video: "js-video",
      videoThumbnail: "js-video-thumbnail",
    },
    flickity: {
      left: "left",
      productImages: "productImages",
    },
    media: {
      isMobile: "max-width: 1024px",
    },
    event: {
      click: "click",
      domContentLoaded: "DOMContentLoaded",
    },
    video: {
      autoplay: "autoplay",
      muted: "muted",
      play: "play",
      pause: "pause",
    },
    searchQuery: "variant",
  };

  const YOUTUBE_POST_MESSAGE =
    '{"event":"command","func":"' + "pauseVideo" + '","args":""}';

  const $videoIframe = document.querySelector(`.${config.dom.video} iframe`);

  let flkty;
  let $videoPlayer;
  let $thumbnails;

  /**
   * Create FLickity Instance
   * @function createFlickityInstance
   * @description Creates flickity instance and uses as-for-nav extention
   */
  const createFlickityInstance = () => {
    const isSmallScreen = window.matchMedia(`(${config.media.isMobile})`);
    const $images = document.querySelector(`#${config.dom.productImages}`);
    const $youtubeVideoIFrame = document.querySelectorAll(
      `.${config.dom.youtube}`
    );

    // Cleanup: Destroy existing Flickity instances before creating new ones
    if (flkty && typeof flkty.destroy === 'function') {
      flkty.destroy();
      flkty = null;
    }

    // Cleanup: Destroy existing thumbnail Flickity instance
    $thumbnails = document.querySelector(`#${config.dom.productThumbnails}`);
    if ($thumbnails) {
      const existingThumbnailFlickity = Flickity.data($thumbnails);
      if (existingThumbnailFlickity) {
        existingThumbnailFlickity.destroy();
      }
    }

    if (!$images || !$thumbnails) return;

    flkty = new Flickity($images, {
      adaptiveHeight: true,
      cellAlign: config.flickity.left,
      contain: true,
      draggable: isSmallScreen.matches ? true : false,
      fade: true,
      pageDots: isSmallScreen.matches ? true : false,
      prevNextButtons: false,
      on: {
        change: () => {
          $youtubeVideoIFrame.forEach((video) => {
            video.contentWindow.postMessage(YOUTUBE_POST_MESSAGE, "*");
          });

          if (!$videoIframe) return;

          if (flkty.selectedElement.classList.contains(config.dom.video)) {
            handleVimeoPausePlay(config.video.play);
          } else {
            handleVimeoPausePlay(config.video.pause);
          }
        },
      },
    });

    new Flickity($thumbnails, {
      asNavFor: `#${config.flickity.productImages}`,
      cellAlign: config.flickity.left,
      contain: true,
      draggable: false,
      groupCells: true,
      pageDots: false,
      setGallerySize: false,
    });
  };

  /**
   * Handle Variant Param Page Load
   * @function handleVariantParamPageLoad
   * @description Handles carousel variant image selection on page load based on URL search param
   * @returns flkty updating carousel to active variant
   */
  const handleVariantParamPageLoad = () => {
    if (!flkty.isActive || flkty.slides.length === 1) return;

    const activeVariant = window.location.search.includes(config.searchQuery)
      ? window.location.search.split("=")[1]
      : null;

    if (!activeVariant) return;

    const $thumbnailVariantImage = document.querySelector(
      `#${config.dom.productThumbnails} [${config.dom.mediaVariantId}="${activeVariant}"]`
    );

    if (!$thumbnailVariantImage) return;

    flkty.select(parseInt($thumbnailVariantImage.dataset.index, 10));
  };

  /**
   * Add Events
   * @function addEvents
   * @description Creates event listeners
   */
  const addEvents = () => {
    if (!flkty) return;

    flkty.element?.classList.remove(config.dom.productImagesLoading);
    $thumbnails?.classList.remove(config.dom.productThumbnailsLoading);

    if (!flkty.isActive || flkty.slides.length === 1) return;

    const $variantButtons = document.querySelectorAll(
      `.${config.dom.productVariantLabel}`
    );

    $variantButtons.forEach(($button) => {
      $button.addEventListener(config.event.click, (event) => {
        const { variantImageId } = event.target.parentElement.dataset;

        const $thumbnailVariantImage = document.querySelector(
          `#${config.dom.productThumbnails} [data-variant="${variantImageId}"]`
        );

        if (!$thumbnailVariantImage) return;

        flkty.select(parseInt($thumbnailVariantImage.dataset.index, 10));
      });
    });
  };

  /**
   * Handle Vimeo Pause Play
   * @param {String} videoAction pause or play
   */
  const handleVimeoPausePlay = (videoAction) => {
    try {
      if (videoAction === config.video.play) {
        $videoPlayer.play();
      } else if (videoAction === config.video.pause) {
        $videoPlayer.pause();
      }
    } catch (error) {
      console.error(
        "Not a valid videoAction param for handleVimeoPausePlay",
        error
      );
    }
  };

  /**
   * Create Vimeo Player
   */
  const createVimeoPlayer = async () => {
    if (!$videoIframe) return;

    $videoPlayer = new Vimeo.Player($videoIframe);
    await $videoPlayer.setVolume(0);
  };

  /**
   * Init
   * @function init
   * @description init function
   */
  const init = () => {
    createFlickityInstance();
    addEvents();
    createVimeoPlayer();
    handleVariantParamPageLoad();
  };

  init();
};

// Initialize gallery on page load
productGallery();

// Listen for reinitialization event (triggered by product swap)
document.addEventListener('gallery:reinit', () => {
  productGallery();
});
